import { Application, Container, RenderTexture, Sprite } from 'pixi.js';
import { KnyttWorld } from '../engine/KnyttWorld';
import { KnyttSave } from '../engine/KnyttSave';
import { KnyttArea } from '../engine/KnyttArea';
import { KnyttPoint } from '../engine/KnyttPoint';
import { JuniValues, PowerNames } from '../engine/JuniValues';
import { bgrToRgb, parseBgrString, parseIniInt } from '../engine/KnyttUtil';
import { AssetManager } from '../render/AssetManager';
import { Tileset } from '../render/Tileset';
import { AreaView, VIEW_H, VIEW_W } from '../render/AreaView';
import { TintFilter, type TintMode } from '../render/TintFilter';
import { CollisionMap } from './CollisionMap';
import { InputManager } from './Input';
import { Player } from './Player';
import { AudioManager } from '../audio/AudioManager';
import { ObjectLayers, prefetchAreaAssets } from './objects/ObjectLayers';
import { DeathPoof } from './DeathPoof';
import { DoubleJumpEffect } from './DoubleJumpEffect';
import { InfoPanel } from './InfoPanel';
import { TitleBanner } from './TitleBanner';
import { BulletManager } from './Bullet';
import { ParticleSystem } from './Particles';


export type CutsceneMode = 'intro' | 'middle' | 'ending'; // GDKnyttDataStore.CutsceneMode

/** Top-level game controller for a single playthrough. */
export class Game {
  readonly app: Application;
  readonly world: KnyttWorld;
  save: KnyttSave; // swapped out by Shift ShiftSave (Shift.cs:203-215)
  readonly assets: AssetManager;
  readonly input: InputManager;
  readonly audio: AudioManager;
  readonly values = new JuniValues();

  // Scene graph
  readonly root = new Container(); // rendered offscreen at an integer prescale
  readonly worldLayer = new Container(); // camera-offset world space
  readonly uiLayer = new Container(); // fixed screen-space overlay (doesn't scroll)
  player!: Player;

  currentArea!: KnyttArea;
  currentView: AreaView | null = null;
  objectLayers: ObjectLayers | null = null;
  collision = new CollisionMap();
  readonly deathPoof = new DeathPoof();
  readonly doubleJumpEffect = new DoubleJumpEffect();
  readonly infoPanel = new InfoPanel(this);
  readonly titleBanner = new TitleBanner();
  private tintFilter: TintFilter | null = null;
  readonly bullets: BulletManager;
  readonly particles: ParticleSystem;

  private tilesetCache = new Map<number, Tileset | null>();
  private scale = 1; // final on-screen scale (fractional)
  private prescale = 1; // integer scale the scene texture is rendered at
  private sceneTexture: RenderTexture;
  private sceneSprite: Sprite;
  onQuit: (() => void) | null = null;
  onWin: ((ending: string) => void) | null = null;
  onPause: (() => void) | null = null;
  onCutscene:
    | ((name: string, onDone: () => void, opts: { sound: string | null; mode: CutsceneMode }) => void)
    | null = null;
  onMap: (() => void) | null = null;
  areaSwim = false;
  shiftLock = false; // blocks chained shifts until Down is released
  paused = false;
  private running = false;
  private respawnTimer = 0;
  private warping = false;

  constructor(app: Application, world: KnyttWorld, save: KnyttSave, dataBaseUrl: string) {
    this.app = app;
    this.world = world;
    this.save = save;
    this.assets = new AssetManager(world, dataBaseUrl);
    this.input = new InputManager();
    // GDKnyttAssetManager.isSongLooped: a song loops only if [Loop Music] N=true.
    this.audio = new AudioManager(
      this.assets,
      (num) => world.iniData.getSection('Loop Music')?.get(String(num))?.trim().toLowerCase() === 'true',
    );
    this.bullets = new BulletManager(this);
    this.particles = new ParticleSystem(this);

    this.root.addChild(this.worldLayer);
    this.worldLayer.sortableChildren = true;
    this.root.addChild(this.uiLayer);
    this.uiLayer.addChild(this.titleBanner.container);
    this.uiLayer.addChild(this.infoPanel.container);

    // The scene renders offscreen into this texture at an integer prescale
    // (so 24px tile sprites round to the same pixel grid — no seams), then the
    // texture is stretched fractionally to fill the window ("sharp bilinear").
    this.sceneTexture = RenderTexture.create({ width: VIEW_W, height: VIEW_H, antialias: false, resolution: 1 });
    this.sceneSprite = new Sprite(this.sceneTexture);
    app.stage.addChild(this.sceneSprite);
  }

  /**
   * Render the scene graph into the offscreen texture. Skipped while paused —
   * the simulation is frozen so the scene can't change — except right after a
   * resize (the texture is recreated and must be repainted once).
   */
  private sceneDirty = true;
  private renderScene = (): void => {
    if (this.paused && !this.sceneDirty) return;
    this.sceneDirty = false;
    this.app.renderer.render({ container: this.root, target: this.sceneTexture });
  };

  async start(): Promise<void> {
    this.save.readInto(this.values);

    const startArea = this.save.getArea();
    const startPos = this.save.getAreaPosition();

    this.player = new Player(this);
    this.worldLayer.addChild(this.player.container);
    await this.player.load();
    // Juni.initialize: restore the persisted attachment + tint before entering the area
    // (checkTint may override the tint if the start area declares one).
    void this.player.setAttachment(this.values.attachment);
    this.applyTint(...this.values.tint);
    this.updateForceMap();

    this.worldLayer.addChild(this.particles.container);
    this.worldLayer.addChild(this.deathPoof.container);
    await this.deathPoof.load(this.assets);
    this.worldLayer.addChild(this.doubleJumpEffect.container);
    await this.doubleJumpEffect.load(this.assets);
    // Bullet containers attach themselves to worldLayer per render z (BulletManager).
    await this.infoPanel.load();

    await this.changeArea(startArea, true);
    this.player.moveToTile(this.currentArea, startPos);
    this.centerCamera();

    // Listen on the renderer, not window: the window resize event fires before
    // Pixi's own resizeTo handler, so reading renderer.width there is stale.
    this.app.renderer.on('resize', this.resize);
    this.resize();

    this.running = true;
    this.app.ticker.add(this.tick);
    this.app.ticker.add(this.renderScene);
  }

  /** Ensure a set of tileset indices are loaded into the local cache. */
  private async ensureTilesets(indices: number[]): Promise<void> {
    await Promise.all(
      indices.map(async (n) => {
        if (this.tilesetCache.has(n)) return;
        const ts = await this.assets.getTileset(n);
        this.tilesetCache.set(n, ts);
      }),
    );
  }

  getTileset = (n: number): Tileset | null => this.tilesetCache.get(n) ?? null;

  getArea = (p: KnyttPoint): KnyttArea | null => this.world.getArea(p);

  async changeArea(newPos: KnyttPoint, force = false, respawn = false): Promise<void> {
    if (!force && this.currentArea && this.currentArea.position.equals(newPos)) return;

    const area = this.world.getArea(newPos);
    if (!area) return;

    const oldAreaSwim = this.objectLayers ? this.areaSwim : false;

    // Preload tilesets + gradients for the new area and its neighbours (for collision).
    const tsNeeded = new Set<number>();
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const na = this.world.getArea(new KnyttPoint(newPos.x + dx, newPos.y + dy));
        if (na && !na.empty) {
          tsNeeded.add(na.tilesetA);
          tsNeeded.add(na.tilesetB);
        }
      }
    }
    await this.ensureTilesets([...tsNeeded]);
    const gradient = await this.assets.getGradient(area.empty ? 0 : area.background);

    // Swap the area view.
    this.bullets.clear();
    this.particles.clear();
    this.player?.stopHologram(true); // holograms don't persist across screens
    // Mark the outgoing area's uncollected items with live values — skipped on respawn
    // (GDKnyttGame.cs:275).
    if (!respawn) this.objectLayers?.checkCollectables(this.values);
    this.currentView?.destroy();
    this.objectLayers?.destroy();
    this.currentArea = area;
    this.currentView = new AreaView(
      area,
      this.getTileset(area.tilesetA),
      this.getTileset(area.tilesetB),
      gradient,
    );
    this.currentView.root.zIndex = 0;
    this.worldLayer.addChild(this.currentView.root);
    if (this.currentView.overlayLayer) {
      this.currentView.overlayLayer.zIndex = 12;
      this.currentView.overlayLayer.x = this.currentView.root.x;
      this.currentView.overlayLayer.y = this.currentView.root.y;
      this.worldLayer.addChild(this.currentView.overlayLayer);
    }

    // Rebuild collision for the 3x3 neighbourhood.
    this.collision.build(newPos, this.getArea, this.getTileset);

    // Objects.
    this.objectLayers = new ObjectLayers(this, area, this.currentView);
    await this.objectLayers.load();

    // Mark the incoming area's uncollected items against the save snapshot's powers,
    // recording into the save's marks (GDKnyttGame.cs:290 — CurrentSave.SourcePowers).
    const sourceValues = new JuniValues();
    this.save.readInto(sourceValues);
    this.objectLayers.checkCollectables(sourceValues);
    this.save.setMarked(sourceValues.marked);

    // Leaving a Swim area into a non-Swim one pulses the swim-exit jump boost
    // (GDKnyttGame.cs:286,294 — Juni.Swim = true; Juni.Swim = false).
    if (oldAreaSwim && !this.areaSwim) this.player.onSwimZoneExit();

    // Attach extra-data equips Juni's attachment sprite; absent = keep the current one.
    const attach = area.getExtraData('Attach');
    if (attach != null) void this.player.setAttachment(attach);

    // Audio.
    this.audio.setMusic(this.objectLayers.playNoMusic ? 0 : area.song);
    this.audio.setAmbiance(0, area.atmosphereA, this.objectLayers.customAmbiance[0]);
    this.audio.setAmbiance(1, area.atmosphereB, this.objectLayers.customAmbiance[1]);

    this.values.setVisited(this.world, area.mapPosition);
    this.centerCamera();
    this.checkTint(area);

    // Warm the cache for the surrounding areas so crossing into one doesn't hitch
    // on first-time gradient/object-sheet decode + GPU upload (tilesets are already
    // covered by ensureTilesets above). Fire-and-forget; AssetManager dedupes.
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const na = this.world.getArea(new KnyttPoint(newPos.x + dx, newPos.y + dy));
        if (na) prefetchAreaAssets(this, na);
      }
    }
  }

  /**
   * Area extra-data Tint/TintInk/TintTrans (GDKnyttGame.cs checkTint). An area
   * without a Tint key keeps the current tint (it persists across areas); an
   * explicit Tint replaces it (Tint=0 clears) and is stored for the save.
   */
  private checkTint(area: KnyttArea): void {
    const color = area.getExtraData('Tint');
    if (color == null) return;
    const ink = area.getExtraData('TintInk') ?? 'Trans';
    const trans = area.getExtraData('TintTrans') ?? '46';
    this.values.tint = [color, ink, trans];
    this.applyTint(color, ink, trans);
  }

  /** GDKnyttGame.cs applyTint: raw strings → filter params; BGR 0 (or no tint) clears. */
  applyTint(colorStr: string | null, inkStr: string | null, transStr: string | null): void {
    const bgr = parseBgrString(colorStr, 0);
    if (bgr === 0) {
      this.worldLayer.filters = [];
      return;
    }
    const modes: Record<string, TintMode> = {
      trans: 'trans',
      add: 'add',
      sub: 'sub',
      and: 'and',
      or: 'or',
      xor: 'xor',
    };
    const mode = modes[(inkStr ?? 'Trans').toLowerCase()] ?? 'trans';
    const trans = parseIniInt(transStr) ?? 46;
    const [r, g, b] = bgrToRgb(bgr);
    const alpha = 1 - trans / 127;
    if (!this.tintFilter) this.tintFilter = new TintFilter();
    this.tintFilter.set(r, g, b, alpha, mode);
    this.worldLayer.filters = [this.tintFilter];
  }

  private centerCamera(): void {
    this.worldLayer.x = -this.currentArea.position.x * VIEW_W;
    this.worldLayer.y = -this.currentArea.position.y * VIEW_H;
  }

  /** Per-area-local origin of the current area in world pixels. */
  get areaOriginX(): number {
    return this.currentArea.position.x * VIEW_W;
  }
  get areaOriginY(): number {
    return this.currentArea.position.y * VIEW_H;
  }

  /** Play an object SFX positioned at world coords (AudioStreamPlayer2D pan/attenuation). */
  sfxAt(name: string, worldX: number, worldY: number): void {
    this.audio.sfx(name, false, { x: worldX - this.areaOriginX, y: worldY - this.areaOriginY });
  }

  /** Pauses world simulation (PausePanel.cs/PauseLayer.cs: GetTree().Paused=true) — music/sfx keep playing, matching yknytt. */
  setPaused(p: boolean): void {
    this.paused = p;
    // PausePanel.unpause / Cutscene.releaseAll: drop the still-held key that closed
    // the modal so it doesn't immediately re-trigger pause/map on the next poll.
    if (!p) this.input.releaseAll();
  }

  /**
   * Whether the map screen opens at all (GDKnyttGame.cs hasMap()). forceMap wins
   * first (set once Juni gains the Map power in a world whose map is otherwise
   * denied — UICanvasLayer.cs:155); then `Map=false` always denies it; otherwise
   * only worlds declaring `Format=4` get it.
   */
  private forceMap = false;
  hasMapAccess(): boolean {
    if (this.forceMap) return true;
    const world = this.world.iniData.getSection('World');
    if (world?.get('Map')?.toLowerCase() === 'false') return false;
    return world?.get('Format') === '4';
  }

  /** UICanvasLayer.cs updatePowers: gaining the Map power forces map access from then on. */
  private updateForceMap(): void {
    if (this.values.getPower(PowerNames.Map) && !this.hasMapAccess()) this.forceMap = true;
  }

  /** Does the player have the Map power (reveals the whole layout, not just visited areas)? */
  hasMapPower(): boolean {
    return this.values.getPower(PowerNames.Map);
  }

  private tick = (): void => {
    if (!this.running || this.paused) return;
    const dt = Math.min(this.app.ticker.deltaMS / 1000, 0.05);
    this.values.totalTime += dt;
    this.input.poll();
    this.deathPoof.update(dt); // keep the poof animating during the respawn freeze
    this.doubleJumpEffect.update(dt);
    this.infoPanel.update(dt);
    this.titleBanner.update(dt);

    if (this.input.justPressed('pause')) {
      this.setPaused(true);
      this.onPause?.();
      return;
    }

    if (this.input.justPressed('map') && this.hasMapAccess()) {
      this.setPaused(true);
      this.onMap?.();
      return;
    }

    if (this.respawnTimer > 0) {
      this.respawnTimer -= dt;
      if (this.respawnTimer <= 0) this.doRespawn();
      return;
    }

    if (this.warping) return; // hold simulation while an area transition resolves

    // GDKnyttKeys.cs debug_die0 (F10) / touch Reset: suicide → normal death/respawn.
    if (this.input.justPressed('debug_die') && !this.player.dead) this.player.die();

    if (!this.input.isHeld('down')) this.shiftLock = false;
    this.player.update(dt);
    this.objectLayers?.update(dt);
    this.bullets.update(dt);
    this.particles.update(dt);
    this.checkWarp();
  };

  setPower(power: PowerNames, value: boolean): void {
    // Juni.cs:405-414 (Format=4 worlds only): revoking Umbrella force-stows a deployed
    // umbrella; revoking Hologram dismisses an active decoy.
    if (this.world.info.format === 4 && !value) {
      if (power === PowerNames.Umbrella && this.player.umbrellaDeployed)
        this.player.umbrellaDeployed = false;
      if (power === PowerNames.Hologram) this.player.stopHologram();
    }
    this.values.setPower(power, value);
    this.updateForceMap();
  }

  onPlayerDied(): void {
    this.values.respawnDeath(this.save.getArea(), this.save.getAreaPosition());
    this.respawnTimer = 1.5; // yknytt RespawnTimer wait_time
  }

  private async doRespawn(): Promise<void> {
    this.deathPoof.clear();
    this.bullets.clear();
    // Restore powers/flags/collectables from the save snapshot, but keep the live
    // run stats and the visited set — JuniValues.cs:95-122 copies TotalDeaths /
    // HardestPlace(Deaths) / TotalTime from the failed (live) values and shares the
    // VisitedAreas reference; only the rest resets from SourcePowers.
    const { totalDeaths, hardestPlace, hardestPlaceDeaths, totalTime, visited } = this.values;
    this.save.readInto(this.values);
    this.values.totalDeaths = totalDeaths;
    this.values.hardestPlace = hardestPlace;
    this.values.hardestPlaceDeaths = hardestPlaceDeaths;
    this.values.totalTime = totalTime;
    this.values.visited = visited;
    this.updateForceMap();
    // Juni.reset(): re-apply the persisted attachment + tint (checkTint may override),
    // and the save's character (a post-save Shift character swap reverts).
    void this.player.setAttachment(this.values.attachment);
    void this.player.changeCharacter(this.values.character ?? this.world.info.character);
    this.applyTint(...this.values.tint);
    const area = this.save.getArea();
    const pos = this.save.getAreaPosition();
    await this.changeArea(area, true, true);
    this.player.respawn();
    this.player.moveToTile(this.currentArea, pos);
    this.centerCamera();
  }

  /** If the player has left the current area, warp to the adjacent area. */
  private checkWarp(): void {
    if (this.warping) return;
    if (this.player.dead) return; // GDKnyttGame.cs:163 — warps are ignored while dead
    // GDKnyttArea.isIn is inclusive: exactly on the 600/240 edge is still inside;
    // warps fire only strictly beyond it.
    const lx = this.player.x - this.areaOriginX;
    const ly = this.player.y - this.areaOriginY;
    if (lx >= 0 && lx <= VIEW_W && ly >= 0 && ly <= VIEW_H) return;
    void this.warpPlayer();
  }

  private async warpPlayer(): Promise<void> {
    if (this.warping) return;
    this.warping = true;
    try {
      await this.doWarp();
    } finally {
      this.warping = false;
    }
  }

  private async doWarp(): Promise<void> {
    const area = this.currentArea;
    // C# getAreaCoords int-casts (truncates toward zero), so crossing the global
    // x=0/y=0 world edge lands back in area 0 — a no-op — instead of area −1.
    const newCoords = new KnyttPoint(
      Math.trunc(this.player.x / VIEW_W),
      Math.trunc(this.player.y / VIEW_H),
    );
    if (newCoords.equals(area.position)) return;

    // Warp offsets only apply when the area contains a Warp object (0,20), which
    // loads them at area load (Warp.cs) — no unconditional loadWarpData here.
    const wc = area.warp.getWarpCoords(newCoords, area.position);

    // Apply warp offset (in area units -> world pixels).
    this.player.x += VIEW_W * wc.x;
    this.player.y += VIEW_H * wc.y;
    const afterWarp = new KnyttPoint(
      Math.trunc(this.player.x / VIEW_W),
      Math.trunc(this.player.y / VIEW_H),
    );

    // Flag warps.
    const fw = this.getFlagWarp(afterWarp);
    if (fw) {
      this.player.x += VIEW_W * fw.x;
      this.player.y += VIEW_H * fw.y;
    }
    const finalCoords = new KnyttPoint(
      Math.trunc(this.player.x / VIEW_W),
      Math.trunc(this.player.y / VIEW_H),
    );

    // Left the world into a non-existent area (e.g. fell through a pit): the
    // level has no destination, so return to the last save instead of falling
    // forever.
    if (!this.world.getArea(finalCoords)) {
      this.warping = false;
      this.player.die(); // poof + sound + respawn, instead of falling forever
      return;
    }

    await this.changeArea(finalCoords);
  }

  getFlagWarp(coords: KnyttPoint): KnyttPoint | null {
    const area = this.world.getArea(coords);
    if (!area) return null;
    const all = area.flagWarps[3];
    let someFailed = false;
    let found: KnyttPoint | null = null;
    for (const fw of area.flagWarps) {
      if (!fw) continue;
      if (this.values.check(fw.flag)) {
        if (fw === all) {
          if (someFailed) continue;
          else found = null;
        }
        if (!found) {
          found = new KnyttPoint(
            fw.xArtifactMode ? this.values.getArtifactsCountGroup(fw.x - 1) : fw.x,
            fw.yArtifactMode ? this.values.getArtifactsCountGroup(fw.y - 1) : fw.y,
          );
        }
      } else {
        someFailed = true;
      }
    }
    return found;
  }

  saveGame(write: boolean, area?: KnyttPoint, position?: KnyttPoint): void {
    // A Shift saves its explicit destination (Shift.cs:195); SavePoint/quit save
    // Juni's current area + tile.
    this.save.setArea(area ?? this.currentArea.position);
    this.save.setAreaPosition(position ?? this.player.areaTile());
    this.save.writeFrom(this.values);
    if (write && this.onSaveWrite) this.onSaveWrite(this.save);
  }
  onSaveWrite: ((save: KnyttSave) => void) | null = null;

  /**
   * Shift ShiftSave(A)=<name> (Shift.cs:203-215): load `savegame<name>.ini` from the
   * world bin as the new current save snapshot (used for future respawns) and persist
   * it. Does not alter the live Juni values. Returns true if the file existed + parsed.
   */
  loadShiftSaveFile(name: string): boolean {
    const bytes = this.world.getFileBytes(`savegame${name}.ini`);
    if (!bytes) return false;
    try {
      this.save = KnyttSave.parse(new TextDecoder().decode(bytes));
      this.onSaveWrite?.(this.save);
      return true;
    } catch {
      return false;
    }
  }

  resize = (): void => {
    // The canvas is sized to the game picture itself (main.ts resizeCanvas);
    // the scene sprite simply fills it. screen.* is in logical pixels.
    const w = this.app.renderer.screen.width;
    const h = this.app.renderer.screen.height;
    this.scale = w / VIEW_W;
    // "Sharp bilinear": the scene is rendered offscreen at the next integer
    // scale up (per-sprite roundPixels rounding stays on one pixel grid — no
    // 1px tile seams), then that texture is linearly downscaled by ≤1x to the
    // exact fractional fit. Crisp pixels, uniform pixel sizes, no seams.
    // The renderer runs at resolution 1 (logical px); the canvas itself is
    // upscaled to device pixels by the compositor (image-rendering:pixelated).
    const k = Math.max(1, Math.ceil(this.scale * this.app.renderer.resolution));
    if (k !== this.prescale) {
      this.prescale = k;
      // Recreate rather than resize() — the sprite keeps the old frame size
      // after an in-place RenderTexture resize.
      const old = this.sceneTexture;
      this.sceneTexture = RenderTexture.create({ width: VIEW_W * k, height: VIEW_H * k, antialias: false, resolution: 1 });
      this.sceneSprite.texture = this.sceneTexture;
      old.destroy(true);
      this.root.scale.set(k);
      this.sceneDirty = true;
      this.renderScene(); // repaint immediately so no stale/blank frame shows
    }
    this.sceneSprite.width = w;
    this.sceneSprite.height = h;
    this.sceneSprite.x = 0;
    this.sceneSprite.y = 0;
  };

  /**
   * StandartSoundPlayer.playSound (StandartSoundPlayer.cs:9-26): `sound` is the
   * already-defaulted name (Switch.cs: "teleport" when absent/empty, null for
   * none/false). door/electronic/switch/teleport (and any unknown name) prefer the
   * world's `Sounds/<name>.ogg`; failing that (and for powerup/bounce/save) the
   * built-in cue plays. Unknown names with no custom file are silent.
   */
  playSwitchSound(sound: string | null): void {
    if (sound == null) return;
    const builtins = ['door', 'electronic', 'switch', 'teleport', 'powerup', 'bounce', 'save'];
    const wavs = ['Door', 'Electronic', 'Switch', 'Teleport', 'Powerup', 'Bounce Platform', 'Save Spot'];
    const idx = builtins.indexOf(sound.toLowerCase());
    // getWorldSound only checks the world bin for idx < 4 or unknown (idx -1).
    if (idx < 4 && this.world.worldFileExists(`Sounds/${sound}.ogg`)) {
      this.audio.sfxFromPath(`Sounds/${sound}.ogg`);
      return;
    }
    if (idx !== -1) this.audio.sfx(wavs[idx]);
  }

  /** Does the world ship a cutscene named `name` (Scene1.png present)? */
  hasCutscene(name: string): boolean {
    return this.world.worldFileExists(`${name}/Scene1.png`);
  }

  /**
   * Pause the game, play a story cutscene, then resume. Game audio is suspended
   * while the cutscene runs (C# detaches the game scene). A Middle cutscene
   * (mode 'middle' — any Shift cutscene) ends with respawnJuni()
   * (Cutscene.cs:127-135): powers reset to the save snapshot and Juni returns to
   * the last save (the Shift's own Save=true already wrote the destination).
   * `sound` is the invoking Shift's resolved sound, played inside the cutscene.
   */
  playCutscene(
    name: string,
    onDone: () => void,
    sound: string | null = null,
    mode: CutsceneMode = 'middle',
  ): void {
    if (this.hasCutscene(name) && this.onCutscene) {
      this.setPaused(true);
      this.audio.suspendForCutscene();
      this.onCutscene(
        name,
        () => {
          this.audio.resumeFromCutscene();
          this.setPaused(false);
          if (mode === 'middle') void this.doRespawn();
          onDone();
        },
        { sound, mode },
      );
    } else {
      onDone();
    }
  }

  win(ending: string): void {
    this.running = false;
    this.values.endings.add(ending);
    // Win.cs:13-28: persist the ending + run stats into the save BEFORE the ending
    // cutscene/teardown — without touching the save's area/position (no respawn move).
    this.save.addEnding(ending);
    this.save.writeStats(this.values);
    this.onSaveWrite?.(this.save);
    // Juni.cs:813 win() stops all Juni audio; the game scene is then torn down.
    this.audio.stopAllGame();
    if (this.hasCutscene(ending) && this.onCutscene) {
      this.onCutscene(ending, () => this.onWin?.(ending), { sound: null, mode: 'ending' });
    } else {
      this.onWin?.(ending);
    }
  }

  destroy(): void {
    this.running = false;
    this.app.ticker.remove(this.tick);
    this.app.ticker.remove(this.renderScene);
    this.app.renderer.off('resize', this.resize);
    this.input.destroy();
    this.audio.destroy();
    this.currentView?.destroy();
    this.objectLayers?.destroy();
    this.player?.destroy();
    this.deathPoof.destroy();
    this.doubleJumpEffect.destroy();
    this.infoPanel.destroy();
    this.titleBanner.destroy();
    this.bullets.destroy();
    this.worldLayer.filters = [];
    this.tintFilter?.destroy();
    this.root.destroy({ children: true });
    this.sceneSprite.destroy();
    this.sceneTexture.destroy(true);
  }
}
